import { useSearchParams } from 'react-router-dom';
import { useCallback, useRef } from 'react';

/**
 * Hook for syncing component state with URL search params.
 * Filters, pagination, and other state survive back/forward navigation.
 *
 * By default, changes PUSH a new history entry (so browser back works).
 * Pass { replace: true } to replace the current entry instead (useful for pagination).
 *
 * @param {object} defaults - Default values by key. Number defaults auto-parse.
 */
export function useURLParams(defaults = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultsRef = useRef(defaults);

  const getParam = useCallback((key) => {
    const value = searchParams.get(key);
    if (value === null) return defaultsRef.current[key] ?? '';
    const defaultVal = defaultsRef.current[key];
    if (typeof defaultVal === 'number') return Number(value);
    return value;
  }, [searchParams]);

  const setParam = useCallback((key, value, { replace = false } = {}) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const defaultVal = defaultsRef.current[key];
      if (value === defaultVal || value === '' || value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
      return next;
    }, { replace });
  }, [setSearchParams]);

  const setParams = useCallback((updates, { replace = false } = {}) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        const defaultVal = defaultsRef.current[key];
        if (value === defaultVal || value === '' || value === null || value === undefined) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace });
  }, [setSearchParams]);

  return { getParam, setParam, setParams, searchParams };
}
